import {
  Icon,
  Text,
  Button,
  TextSizes,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import { t } from "i18next";

import { TriggerResultGroup } from "../components/TriggerResultGroup/TriggerResultGroup";

import type {
  ActionGroupStatus,
  ActionOutputData,
  Job,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import {
  JobStatus_Stage,
  Timestamp,
  TriggerDescription_TriggerRetrySupport,
} from "@smartsuite/automation-engine-api";
import type { Weight } from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { useRootStore } from "../../../../stores/hooks";
import { ActionLabel } from "../../../../utils/constants";
import {
  formatDateByUserPreferences,
  getTimeFormatByUserPreferences,
} from "../../../../utils/date";
import { convertStatus } from "../../ActivityGrid/ActivityGrid.helpers";
import { useJobRetry } from "../../job-retry.hook";
import { FindRecordsGroup } from "../components/FindRecordsGroup/FindRecordsGroup";
import { GeneralActionResultGroup } from "../components/GeneralActionResultGroup/GeneralActionResultGroup";
import { StyledHeader, StyledTitle, StyledWrapper } from "./styles";
import { MergeRecordsGroup } from "../components/MergeRecordsGroup/MergeRecordsGroup";
import { DeleteRecordsGroup } from "../components/DeleteRecordsGroup/DeleteRecordsGroup";
import { GeneratePdfGroup } from "../components/GeneratePdfGroup/GeneratePdfGroup";
import { GenerateWebhookGroup } from "../components/GenerateWebhookGroup/GenerateWebhookGroup";
import { DocsAutomatorGroup } from "../components/DocsAutomatorGroup";
import { Colors, GrayColors, IconColorNames } from "@smartsuite/types";
import { AIActionResultGroup } from "../components/AIActionResultGroup/AIActionResultGroup";
import { FindLoopingRecordsGroup } from "../components/LoopingActionsGroup/components/FindLoopingRecordsGroup/FindLoopingRecordsGroup";
import { NestedGroupsRenderer } from "../components/NestedGroupsRenderer/NestedGroupsRenderer";
import { LoopingBlock } from "../components/LoopingBlock/LoopingBlock";
import { BranchesGroup } from "../components/BranchesGroup/BranchesGroup";
import { useRecordJobData } from "../hooks/useRecordJobData";
import { getJobError } from "./get-job-error";
import { orderStatusGroupsByConfig } from "../../shared/status-order";
import { isOldTypejob } from "../utils";
import { MergeLoopingRecordsGroup } from "../components/LoopingActionsGroup/components/MergeLoopingRecordsGroup/MergeLoopingRecordsGroup";
import {
  getMsTeamsActionableNotificationInputValue,
  MS_TEAMS_ACTION_SEND_MESSAGE_ID,
  MS_TEAMS_SEND_ACTIONABLE_NOTIFICATION_ACTION_ID,
  shouldDisplayMsTeamsActionableNotificationInput,
} from "../components/MsTeamsActionableNotificationGroup/get-input-value";

const MS_TEAMS_GENERAL_ACTION_IDS = [
  MS_TEAMS_SEND_ACTIONABLE_NOTIFICATION_ACTION_ID,
  MS_TEAMS_ACTION_SEND_MESSAGE_ID,
];

interface ActivityDetailsProps {
  onGoBack: () => void;
}

const getJobActions = (job: Job): ActionOutputData[] => {
  if (job.actionGroupsStatus?.length > 0) {
    return job.actionGroupsStatus.flatMap((group) => {
      if (group.status.oneofKind === "actions") {
        return group.status.actions.actions;
      }

      return [];
    });
  }

  return job.actions;
};

export const ActivityDetails = observer<ActivityDetailsProps>(
  ({ onGoBack }) => {
    const {
      config: { userPreferences },
      automations,
      jobDetails,
      limits,
    } = useRootStore();

    const { isJobCanBeRetried } = useJobRetry();

    const [recordJobData, setRecordJobData] = useState<
      Record<string, RecordJobData>
    >({});
    const { fetchRecordJobData } = useRecordJobData({
      recordJobData,
      setRecordJobData,
    });

    const { retry } = useJobRetry();
    const [disabled, setDisabled] = useState(false);

    const tooltipTextKey = limits.isOverLimit()
      ? limits.canPlanBeUpgraded()
        ? "retry.tooltip-limit-reached-upgrade"
        : "retry.tooltip-limit-reached"
      : "retry.automation-cant-be-rerun";

    const { job } = jobDetails;

    // Stable identities: NestedGroupsRenderer's children are observer(memo)
    // components, so fresh closures per render would cascade re-renders (and
    // config/status tree re-walks) through every expanded block.
    const handleDetailsClick = useCallback(
      (recordJobId: string, fallbackRecordJobId?: string): void => {
        void fetchRecordJobData(recordJobId, fallbackRecordJobId);
      },
      [fetchRecordJobData],
    );

    const renderNestedGroups = useCallback(
      (
        nested: ActionGroupStatus[],
        index: number,
        insideLoop?: boolean,
      ): ReactNode => (
        <NestedGroupsRenderer
          groups={nested}
          job={job}
          recordIndex={index}
          recordJobData={recordJobData}
          onDetailsClick={handleDetailsClick}
          insideLoop={insideLoop ?? false}
        />
      ),
      [job, recordJobData, handleDetailsClick],
    );

    if (job.triggerTime === undefined) {
      return null;
    }

    const jobdCanBeRetried = isJobCanBeRetried(job);

    const showRerunButton = [
      JobStatus_Stage.ACTION_FAIL,
      JobStatus_Stage.TRIGGER_FAIL,
      JobStatus_Stage.AUTOMATION_DISABLED,
    ].includes(job.status?.stage ?? JobStatus_Stage.STARTED);

    const date = Timestamp.toDate(
      job.retryStatus?.retryAt === undefined
        ? job.triggerTime
        : job.retryStatus.retryAt,
    );
    const formattedDate = `${formatDateByUserPreferences(
      date,
      automations.candidate.getTimezone(),
      "MMM D, yyyy",
    )} at ${formatDateByUserPreferences(
      date,
      automations.candidate.getTimezone(),
      getTimeFormatByUserPreferences(userPreferences),
    )}`;
    const status = convertStatus(job);

    const retryJob = async (): Promise<void> => {
      setDisabled(true);
      await retry(job.jobId, onGoBack, () => {
        setDisabled(false);
      });
    };

    const actions = getJobActions(job);
    const jobError = getJobError(job);
    // NOT the incoming order: actionGroupsStatus arrives keyed by instanceId,
    // i.e. group CREATION order, so a group inserted above an existing block
    // would render after it (STR-14248) -- see orderStatusGroupsByConfig.
    const orderedGroups = orderStatusGroupsByConfig(
      job.actionGroupsStatus ?? [],
      job.automation?.actionGroups ?? [],
    );

    // Extracted so the top-level "actions" groups can be dispatched
    // per-action from the ordered groups walk below, without duplicating the
    // per-actionId dispatch logic. `groupInstanceId` is the id of the
    // top-level group the action came from -- record-job paths are keyed by
    // it, so a group that follows a loop/branches block must not fall back
    // to group-0 (the legacy path, where no groups exist, passes 0).
    const renderTopLevelAction = (
      action: ActionOutputData,
      index: number,
      groupInstanceId = 0,
    ): ReactNode => {
      if (!action.actionReference) {
        return null;
      }

      const actionId = action.actionReference.actionId;
      const key = action.actionReference.instanceId ?? `action-${index}`;
      if (actionId === ActionLabel.findRecords) {
        const isGroupRequireOutputs = job.actionGroupsStatus?.some(
          (group) =>
            group?.status?.oneofKind === "actions" &&
            group?.status?.actions?.actions?.some(
              (groupAction) =>
                groupAction.actionReference?.instanceId ===
                action.actionReference?.instanceId,
            ),
        );

        if (isGroupRequireOutputs) {
          const recordJodId = `jobs/${job.jobId}/group-${groupInstanceId}/action-${action.actionReference?.instanceId}/record-0`;

          return (
            <FindLoopingRecordsGroup
              key={key}
              action={action}
              recordJobDataByDetailsId={recordJobData}
              onDetailsClick={() => {
                void fetchRecordJobData(recordJodId);
              }}
              recordJobData={recordJobData[recordJodId]}
            />
          );
        }
        return <FindRecordsGroup key={key} action={action} />;
      } else if (actionId === ActionLabel.mergeRecords) {
        if (!isOldTypejob(jobDetails)) {
          return (
            <MergeLoopingRecordsGroup
              key={key}
              jobId={job.jobId}
              groupInstanceId={groupInstanceId}
              recordListCount={action.recordListCount}
              action={action}
              onDetailsClick={(recordJodId) => {
                void fetchRecordJobData(recordJodId);
              }}
              recordsJobData={recordJobData}
            />
          );
        } else {
          return <MergeRecordsGroup key={key} action={action} />;
        }
      } else if (actionId === ActionLabel.deleteRecord) {
        return <DeleteRecordsGroup key={key} action={action} />;
      } else if (actionId === ActionLabel.generatePdf) {
        return <GeneratePdfGroup key={key} action={action} />;
      } else if (actionId === ActionLabel.docsautomator) {
        return <DocsAutomatorGroup key={key} action={action} />;
      } else if (actionId === ActionLabel.sendWebhook) {
        return <GenerateWebhookGroup key={key} action={action} />;
      } else if (actionId === ActionLabel.ai) {
        const recordJodId = `jobs/${job.jobId}/group-${groupInstanceId}/action-${action.actionReference?.instanceId}/record-0`;
        return (
          <AIActionResultGroup
            key={key}
            action={action}
            onDetailsClick={() => {
              void fetchRecordJobData(recordJodId);
            }}
            recordJobData={recordJobData[recordJodId]}
          />
        );
      } else if (MS_TEAMS_GENERAL_ACTION_IDS.includes(actionId)) {
        return (
          <GeneralActionResultGroup
            key={key}
            action={action}
            groupInstanceId={groupInstanceId}
            getInputValue={getMsTeamsActionableNotificationInputValue}
            shouldDisplayInput={(input, recordJob) =>
              shouldDisplayMsTeamsActionableNotificationInput(
                input,
                jobDetails,
                recordJob,
              )
            }
          />
        );
      } else {
        return (
          <GeneralActionResultGroup
            key={key}
            action={action}
            groupInstanceId={groupInstanceId}
          />
        );
      }
    };

    const hasActionGroups = (job.actionGroupsStatus?.length ?? 0) > 0;

    return (
      <StyledWrapper>
        <StyledHeader status={status}>
          <StyledTitle>
            {status === "is_success" && (
              <Icon name="check-circle" color={Colors.bGreen} />
            )}
            {status === "is_error" && (
              <Icon name="close-circle" color={Colors.bRed} />
            )}
            {status === "is_pending" && (
              <Icon name="arc-7-10" color={Colors.bOrange} />
            )}
            {status === "conditions_not_met" && (
              <Icon name="check-circle" color={Colors.bPurple} />
            )}
            {status === "rejected" && (
              <Icon name="close-circle" color={Colors.bPink} />
            )}
            <Text size="l" styles={{ marginLeft: 10, marginRight: 10 }}>
              {status === "is_success" && t("job-list.ran-successfully")}
              {status === "is_error" && t("job-list.failed-to-run")}
              {status === "is_pending" && t("job-list.running")}
              {status === "conditions_not_met" &&
                t("job-list.conditions-not-met")}
              {status === "rejected" && t("job-list.rejected-description")}
            </Text>
            {jobdCanBeRetried && (
              <Tooltip
                padding={2}
                text={t("retry.rerun-description")}
                placements={["top"]}
                target={
                  <Button
                    iconName="refresh"
                    disabled={disabled}
                    styles={{
                      background: GrayColors["G-0"],
                      textColor: IconColorNames.dark,
                      height: 26,
                      sidePadding: 12,
                      textSize: TextSizes.S,
                      borderRadius: 5,
                      borderWidth: 1,
                      borderColor: GrayColors["G-1-5"],
                      borderHoverColor: GrayColors["G-2"],
                      textWeight: "semibold" as Weight,
                      iconRightIndent: 7,
                      iconLeftIndent: 0,
                      iconColor: IconColorNames.dark,
                    }}
                    onClick={() => {
                      void retryJob();
                    }}
                  >
                    {t("retry.rerun-automation")}
                  </Button>
                }
              />
            )}

            {showRerunButton && !jobdCanBeRetried && (
              <Tooltip
                padding={2}
                text={t(tooltipTextKey)}
                placements={["top"]}
                target={
                  <Button
                    iconName="refresh"
                    styles={{
                      background: GrayColors["G-1"],
                      textColor: IconColorNames.dark,
                      height: 26,
                      sidePadding: 12,
                      textSize: TextSizes.S,
                      borderRadius: 5,
                      borderWidth: 1,
                      borderColor: GrayColors["G-1-5"],
                      textWeight: "semibold" as Weight,
                      iconRightIndent: 7,
                      iconLeftIndent: 0,
                      iconColor: IconColorNames.dark,
                    }}
                  >
                    {t("retry.rerun-automation")}
                  </Button>
                }
              />
            )}
          </StyledTitle>
          <Text size="l">{formattedDate}</Text>
        </StyledHeader>
        {jobError !== "" && (
          <StyledHeader status={status}>
            <StyledTitle>
              <Icon name="important-lined" color={Colors.bRed} />
              <Text size="l" styles={{ marginLeft: 10, marginRight: 10 }}>
                {jobError}
              </Text>
            </StyledTitle>
          </StyledHeader>
        )}

        <TriggerResultGroup job={job} triggerTime={formattedDate} />
        {hasActionGroups
          ? // The groups are walked in order so a top-level "actions" group
            // renders its actions, a "loop"/"branches" group renders its
            // block, interleaved exactly as the automation executed them
            // -- otherwise a post-block action would render before the block
            // that precedes it, misrepresenting execution order.
            orderedGroups.map((group) => {
              if (group.status.oneofKind === "actions") {
                return group.status.actions.actions.map((action, index) =>
                  renderTopLevelAction(action, index, group.instanceId),
                );
              }
              if (group.status.oneofKind === "loop") {
                return (
                  <LoopingBlock
                    key={`loop-${group.instanceId}`}
                    statusGroup={group}
                    job={job}
                    renderNestedGroups={renderNestedGroups}
                    recordJobData={recordJobData}
                    fetchRecordJob={handleDetailsClick}
                  />
                );
              }
              if (group.status.oneofKind === "branches") {
                return (
                  <BranchesGroup
                    key={`branches-${group.instanceId}`}
                    statusGroup={group}
                    job={job}
                    renderNestedGroups={renderNestedGroups}
                  />
                );
              }
              return null;
            })
          : actions.map((action, index) => renderTopLevelAction(action, index))}
      </StyledWrapper>
    );
  },
);
